<template>
  <component
    :is="rootTag"
    class="base-chip"
    :type="rootTag === 'button' ? 'button' : null"
    :class="[
      `base-chip--${tone}`,
      `base-chip--${preset}`,
      `base-chip--${size}`,
      {
        'base-chip--interactive': isInteractive,
        'base-chip--selected': isSelected,
        'base-chip--block': block,
        'is-disabled': disabled,
      },
    ]"
    :disabled="rootTag === 'button' && disabled ? true : null"
    :role="rootRole"
    :tabindex="rootTabindex"
    :aria-label="!hasText ? ariaLabel : null"
    v-bind="ariaState"
    @click="onClick"
    @keydown.enter="onKeyActivate"
    @keydown.space="onKeyActivate"
  >
    <!-- [Icon] — leading, optional. Slot overrides the icon prop for custom marks. -->
    <slot name="leading" />
    <BaseIcon
      v-if="icon && !$slots.leading"
      :name="icon"
      :color="chipIconColor"
      class="base-chip__icon base-chip__icon--lead"
    />

    <!-- [Text] — optional -->
    <span v-if="hasText" class="base-chip__label">
      <slot />
    </span>

    <!-- [Count] — optional badge after the label (e.g. "N items in this chip"). -->
    <span v-if="count" class="base-chip__count">{{ count }}</span>

    <!-- [Icon] — trailing, optional. Yields to check / clear / #trailing. -->
    <transition name="base-chip-check">
      <span v-if="showCheck" class="base-chip__icon base-chip__icon--trail base-chip__check" aria-hidden="true">
        <BaseIcon name="check" :color="chipIconColor" />
      </span>
    </transition>
    <button
      v-if="clearable"
      type="button"
      class="base-chip__icon base-chip__icon--trail base-chip__close"
      :aria-label="clearLabel"
      :disabled="disabled"
      @click.stop="onClear"
      @keydown.enter.stop
      @keydown.space.stop
    >
      <BaseIcon name="x" :color="chipIconColor" />
    </button>
    <!-- Custom trailing action (e.g. a menu button). Stops propagation so it
         doesn't trigger the chip's primary click. -->
    <span v-if="$slots.trailing" class="base-chip__trailing" @click.stop @keydown.enter.stop @keydown.space.stop>
      <slot name="trailing" />
    </span>
    <BaseIcon
      v-if="showTrailingIcon"
      :name="iconRight"
      :color="chipIconColor"
      class="base-chip__icon base-chip__icon--trail"
    />
  </component>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import { isTone, isPreset } from '@/util/design-tokens';

const SIZES = ['sm', 'md', 'lg'];

export default {
  name: 'BaseChip',
  components: {
    BaseIcon,
  },
  model: {
    prop: 'selected',
    event: 'update:selected',
  },
  props: {
    // [Icon] leading — optional (or use the #leading slot for custom marks).
    icon: {
      type: String,
      default: null,
    },
    // [Icon] trailing — optional custom icon. Yields to check / clear / #trailing.
    iconRight: {
      type: String,
      default: null,
    },
    tone: {
      type: String,
      default: 'neutral',
      validator: isTone,
    },
    preset: {
      type: String,
      default: 'soft',
      validator: isPreset,
    },
    // Optional count badge rendered after the label (before the trailing
    // check/clear); shown only when > 0. E.g. a chip standing in for N items.
    count: {
      type: Number,
      default: null,
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => SIZES.includes(v),
    },
    // Selected state. Interactive chips also show a check (unless clearable).
    selected: {
      type: Boolean,
      default: false,
    },
    // Removable: shows a trailing X that emits `clear`. The body stays
    // interactive (rendered as a focusable role="button" container).
    clearable: {
      type: Boolean,
      default: false,
    },
    // Full-width block layout (label grows, trailing content sits at the end).
    block: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: null,
    },
    clearLabel: {
      type: String,
      default: 'Clear',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Hover/press motion. Default on; set false to keep the chip visually static.
    interactive: {
      type: [Boolean, String],
      default: true,
    },
  },
  computed: {
    // Same design token as label text; pass to BaseIcon so stroke/fill using currentColor match the chip.
    chipIconColor() {
      return 'var(--action-text)';
    },
    hasText() {
      return !!this.$slots.default;
    },
    isInteractive() {
      // HTML `interactive="false"` is the string "false" (Boolean("false") === true),
      // so treat that the same as a real boolean false.
      return this.interactive !== false && this.interactive !== 'false';
    },
    // A secondary control (clear X or #trailing action) forces a non-button root
    // so we never nest a <button> inside a <button>.
    hasSecondaryAction() {
      return this.clearable || !!this.$slots.trailing;
    },
    rootTag() {
      return this.isInteractive && !this.hasSecondaryAction ? 'button' : 'div';
    },
    // Native <button> needs no role; an interactive <div> needs an explicit one.
    rootRole() {
      return this.rootTag === 'div' && this.isInteractive ? 'button' : null;
    },
    rootTabindex() {
      if (this.rootTag !== 'div' || !this.isInteractive) return null;
      return this.disabled ? -1 : 0;
    },
    isSelected() {
      return !!this.selected;
    },
    // Check mark for selected interactive chips; clearable chips already have an X.
    showCheck() {
      return this.isSelected && this.isInteractive && !this.clearable;
    },
    showTrailingIcon() {
      return !!this.iconRight && !this.showCheck && !this.clearable && !this.$slots.trailing;
    },
    ariaState() {
      if (!this.isInteractive || !this.isSelected) return {};
      return { 'aria-pressed': 'true' };
    },
  },
  methods: {
    onClick(event) {
      if (this.disabled || !this.isInteractive) return;
      this.$emit('click', event);
    },
    // A native <button> activates on Enter/Space itself; an interactive <div>
    // (root has a secondary control) must be activated manually.
    onKeyActivate(event) {
      if (this.rootTag === 'button') return;
      if (event.key === ' ' || event.code === 'Space') event.preventDefault();
      this.onClick(event);
    },
    onClear(event) {
      if (this.disabled) return;
      this.$emit('clear', event);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.base-chip {
  display: inline-flex;
  width: max-content;
  align-items: center;
  box-sizing: border-box;
  font-family: $font-family-body;
  font-weight: $font-weight-medium;
  line-height: 1.2;
  white-space: nowrap;

  // `background` (not `background-color`) so the AI tone's gradient fill is valid.
  background: var(--action-bg);
  color: var(--action-text);
  border: 1px solid var(--action-border);

  user-select: none;
  outline: none;
  cursor: default;
  transition: background-color $duration-fast $ease-ui-interaction, border-color $duration-fast $ease-ui-interaction,
    box-shadow $duration-fast $ease-ui-interaction, transform $duration-fast $ease-spring;

  // ── Size (height, padding, radius, gap, font-size, icon sizing) ──
  // Sized manually rather than via the action-size mixin: that mixin forces
  // `fill: currentColor` on every svg, which overrides feather icons'
  // `fill="none"` and fills the check/chevron/tag into solid blobs.
  //
  // `sm` deliberately mirrors BaseTag's box (min-height/padding/gap from `xs`,
  // font from `sm`) so chips and tags line up when placed together; only the
  // radius stays the chip's soft-square step. md/lg are the larger options.
  &--sm {
    min-height: map-get(map-get($aloha-sizes, 'xs'), 'height');
    min-width: map-get(map-get($aloha-sizes, 'xs'), 'min-width');
    gap: map-get(map-get($aloha-sizes, 'xs'), 'gap');
    font-size: map-get(map-get($aloha-sizes, 'sm'), 'font-size');
    padding: map-get(map-get($aloha-sizes, 'xs'), 'py') map-get(map-get($aloha-sizes, 'xs'), 'px-round');
    border-radius: map-get(map-get($aloha-sizes, 'sm'), 'radius');

    .base-chip__icon {
      width: map-get(map-get($aloha-sizes, 'xs'), 'icon-size');
      height: map-get(map-get($aloha-sizes, 'xs'), 'icon-size');
    }
  }

  $chip-sizes: ('md', 'lg');

  @each $size in $chip-sizes {
    $size-map: map-get($aloha-sizes, $size);

    &--#{$size} {
      height: map-get($size-map, 'height');
      min-width: map-get($size-map, 'min-width');
      gap: map-get($size-map, 'gap');
      font-size: map-get($size-map, 'font-size');
      padding: map-get($size-map, 'py') map-get($size-map, 'px');
      border-radius: map-get($size-map, 'radius');

      .base-chip__icon {
        width: map-get($size-map, 'icon-size');
        height: map-get($size-map, 'icon-size');
      }
    }
  }

  &--block {
    width: 100%;

    .base-chip__label {
      flex: 1 1 auto;
    }
  }

  &--interactive {
    cursor: pointer;

    // Lift on hover, press-in on click.
    &:hover:not(.is-disabled) {
      box-shadow: $shadow-sm;
      transform: translateY(-1px);
    }

    // Unselected chips warm their background on hover (tonal for soft chips).
    &:hover:not(.is-disabled):not(.base-chip--selected) {
      background: var(--action-bg-hover);
    }

    &:active:not(.is-disabled) {
      box-shadow: none;
      transform: scale($transform-scale-active);
    }
  }

  &:focus-visible {
    box-shadow: $shadow-focus;
  }

  @each $preset in $aloha-presets {
    @each $tone, $_ in $aloha-tones {
      &--#{$preset}.base-chip--#{$tone} {
        @include action-preset-hooks($preset, $tone);
      }
    }
  }

  &.is-disabled {
    opacity: $opacity-disabled;
    cursor: not-allowed;
    pointer-events: none;
  }

  &__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Count badge: a compact white pill sitting on the chip's fill.
  &__count {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    min-width: map-get(map-get($aloha-sizes, 'xxs'), 'height');
    height: map-get(map-get($aloha-sizes, 'xxs'), 'height');
    padding: 0 map-get(map-get($aloha-sizes, 'xxs'), 'px');
    border-radius: $radius-full;
    background-color: $color-white;
    color: var(--action-text);
    font-size: map-get(map-get($aloha-sizes, 'xs'), 'font-size');
    font-weight: $font-weight-bold;
    line-height: 1;
  }

  &__check {
    transform-origin: center;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    // Nested svgs (the check span, the clear button) fill their sized box.
    svg {
      width: 100%;
      height: 100%;
    }
  }

  &__trailing {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  // ── Clearable close button ─────────────────────────────────────
  // X rotates 90° on hover — 90 (not 180) because 180 lands on itself
  // and reads as no motion.
  &__close {
    padding: 0;
    margin: 0;
    background: transparent;
    border: 0;
    border-radius: $radius-xs;
    color: inherit;
    cursor: pointer;
    outline: none;
    transition: background-color $duration-fast $ease-ui-interaction, box-shadow $duration-fast $ease-ui-interaction;

    svg {
      transition: transform $duration-pop $ease-spring;
    }

    &:hover:not(:disabled) {
      background-color: $color-shadow-dark;

      svg {
        transform: rotate(90deg);
      }
    }

    &:focus-visible {
      box-shadow: $shadow-focus;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;

    .base-chip__close svg {
      transition-duration: 0ms;
      transform: none !important;
    }
  }
}

// Checkmark pop-in — uses the DS motion-pop keyframe.
.base-chip-check-enter-active {
  animation: motion-pop $duration-pop $ease-spring;
}

.base-chip-check-leave-active {
  transition: transform $duration-fast $ease-ui-interaction, opacity $duration-fast $ease-ui-interaction;
}

.base-chip-check-leave-to {
  transform: scale(0.4);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .base-chip-check-enter-active,
  .base-chip-check-leave-active {
    animation: none;
    transition-duration: 0ms;
  }
}
</style>
